import { apiClient } from './api'

export interface ProjectScopeItem {
  id: number
  project_id: number
  category: string
  item: string
  description: string | null
  status: string
  deliverables: string[] | null
  acceptance_criteria: string[] | null
  owner: string | null
  position: number
  created_at: string
  updated_at: string
}

export interface ProjectScopeItemCreate {
  category: string
  item: string
  description?: string
  status?: string
  deliverables?: string[]
  acceptance_criteria?: string[]
  owner?: string
  position?: number
}

export interface ProjectOutOfScope {
  id: number
  project_id: number
  item_text: string
  position: number
  created_at: string
}

export interface ProjectOutOfScopeCreate {
  item_text: string
  position?: number
}

export interface ProjectChangeRequest {
  id: number
  project_id: number
  description: string
  category: string
  reason: string | null
  impact_analysis: Record<string, unknown> | null
  approver: string | null
  status: string
  submitted_date: string | null
  created_at: string
  updated_at: string
}

export interface ProjectChangeRequestCreate {
  description: string
  category: string
  reason?: string
  impact_analysis?: Record<string, unknown>
  approver?: string
  status?: string
  submitted_date?: string
}

export const projectScopeService = {
  listScopeItems(projectId: number): Promise<ProjectScopeItem[]> {
    return apiClient.get(`/api/v1/projects/${projectId}/scope/items`).then((r) => r.data)
  },
  createScopeItem(projectId: number, body: ProjectScopeItemCreate): Promise<ProjectScopeItem> {
    return apiClient.post(`/api/v1/projects/${projectId}/scope/items`, body).then((r) => r.data)
  },
  updateScopeItem(projectId: number, itemId: number, body: Partial<ProjectScopeItemCreate>): Promise<ProjectScopeItem> {
    return apiClient.patch(`/api/v1/projects/${projectId}/scope/items/${itemId}`, body).then((r) => r.data)
  },
  deleteScopeItem(projectId: number, itemId: number): Promise<void> {
    return apiClient.delete(`/api/v1/projects/${projectId}/scope/items/${itemId}`)
  },

  listOutOfScope(projectId: number): Promise<ProjectOutOfScope[]> {
    return apiClient.get(`/api/v1/projects/${projectId}/scope/out-of-scope`).then((r) => r.data)
  },
  createOutOfScope(projectId: number, body: ProjectOutOfScopeCreate): Promise<ProjectOutOfScope> {
    return apiClient.post(`/api/v1/projects/${projectId}/scope/out-of-scope`, body).then((r) => r.data)
  },
  deleteOutOfScope(projectId: number, itemId: number): Promise<void> {
    return apiClient.delete(`/api/v1/projects/${projectId}/scope/out-of-scope/${itemId}`)
  },

  listChangeRequests(projectId: number): Promise<ProjectChangeRequest[]> {
    return apiClient.get(`/api/v1/projects/${projectId}/scope/change-requests`).then((r) => r.data)
  },
  createChangeRequest(projectId: number, body: ProjectChangeRequestCreate): Promise<ProjectChangeRequest> {
    return apiClient.post(`/api/v1/projects/${projectId}/scope/change-requests`, body).then((r) => r.data)
  },
  updateChangeRequest(projectId: number, reqId: number, body: Partial<ProjectChangeRequestCreate>): Promise<ProjectChangeRequest> {
    return apiClient.patch(`/api/v1/projects/${projectId}/scope/change-requests/${reqId}`, body).then((r) => r.data)
  },
  deleteChangeRequest(projectId: number, reqId: number): Promise<void> {
    return apiClient.delete(`/api/v1/projects/${projectId}/scope/change-requests/${reqId}`)
  },

  // ── Part 21 — AI scope drafter ─────────────────────────────────────────────
  aiDraft(
    projectId: number,
    body: { objective?: string; key_objectives?: string[]; industry?: string } = {},
  ): Promise<ScopeAIDraft> {
    return apiClient
      .post(`/api/v1/projects/${projectId}/scope/ai-draft`, body)
      .then((r) => r.data)
  },

  acceptDrafts(projectId: number, body: ScopeAcceptRequest): Promise<ScopeAcceptResponse> {
    return apiClient
      .post(`/api/v1/projects/${projectId}/scope/accept`, body)
      .then((r) => r.data)
  },
}

export interface ScopeDraftMilestone {
  name: string
  description: string
  due_date_offset_days: number
}

export interface ScopeAIDraft {
  milestones: ScopeDraftMilestone[]
  success_criteria: string[]
  out_of_scope: string[]
}

export interface ScopeAcceptRequest {
  milestones?: ScopeDraftMilestone[]
  success_criteria?: string[]
  out_of_scope?: string[]
  replace_success_criteria?: boolean
}

export interface ScopeAcceptResponse {
  milestones_created: number
  success_criteria_total: number
  out_of_scope_created: number
}
